import type { ReactNode } from 'react';

import { EmptyState } from '../Card';
import { Drawer } from '../Drawer';

export interface AssistantDrawerProps {
  /** Whether the drawer is open. */
  readonly open: boolean;
  /** Called when the drawer is dismissed. */
  readonly onClose: () => void;
  /** Which page the assistant is on, shown in the header. */
  readonly context?: string;
  /** The assistant itself, supplied by the feature layer. */
  readonly children?: ReactNode;
}

/**
 * The chrome the assistant lives in, mountable on any screen.
 *
 * Inline rather than modal, and that is a product decision rather than a
 * styling one. A modal assistant would cover the record it is talking about and
 * take the keyboard, which trains people to close it before acting — the exact
 * opposite of "assists". Inline, it is a complementary landmark: the page
 * behind it stays live, the reviewer can check a claim against the record while
 * the claim is still on screen, and focus is never stolen.
 *
 * The footer states the operating model on every screen it appears on. It is
 * not decoration: the one thing a user must never have to infer is whether
 * something they are reading has been decided by a person.
 *
 * The drawer is deliberately presentational. Loading a record and talking to
 * the server belong to the feature layer, which supplies the contents as
 * children. This keeps catalogue stories and other consumers usable without a
 * query provider and keeps the component library independent of the API.
 *
 * @param props see {@link AssistantDrawerProps}
 * @returns the drawer, or nothing when closed
 */
export function AssistantDrawer({
  open,
  onClose,
  context,
  children,
}: AssistantDrawerProps): React.JSX.Element | null {
  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Assistant"
      side="end"
      modal={false}
      footer={
        <p className="fo-assistant__footer">
          The assistant reads records and drafts text. It never decides, prices, diagnoses or
          contacts a customer. Every action it proposes waits for a named human.
        </p>
      }
    >
      {context === undefined ? null : (
        <p className="fo-assistant__context">
          Looking at <strong>{context}</strong>
        </p>
      )}

      {children ?? (
        <EmptyState
          icon="assistant"
          title="Nothing to show yet"
          description="Assistant workflows are not available on this screen yet. This panel holds no conversation and sends nothing."
        />
      )}
    </Drawer>
  );
}
